import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Shared settings body for the three communication widgets (NutriMail,
// eHealth mail and ZorgMail). All three render the same two-part body from a
// different source, so they share one settings shape — three independently
// toggleable parts under one composite value key:
//   { counts: boolean, list: boolean, cta: boolean }
// - `counts`: the "x ontvangen · y verzonden" summary line. On by default.
// - `list`  : the newest-5 mails list. On by default.
// - `cta`   : the top-right goto button to the source's inbox. On by default.
// No per-row visibility settings — the mail rows are data, not config — and
// no order setting. Three widget-level switches in one Paper block.

export type CommunicationSmallSettingsValue = {
  counts?: boolean;
  list?: boolean;
  cta?: boolean;
};

type Props = {
  value: CommunicationSmallSettingsValue;
  onChange: (v: CommunicationSmallSettingsValue) => void;
  // Unused for these widgets (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

// One component, three label bases — every source describes its own popup.
export const communicationSmallSettings = (labelBase: string) => {

  const Component: FunctionComponent<Props> = ({
    value,
    onChange
  }) => {
    const theme = useTheme();
    const {t} = useTranslation(['translations']);

    // Defensive defaults so the dialog works before the first save.
    const counts = HelpersClientWidgets.getCommunicationSmallCountsEnabled(value);
    const list = HelpersClientWidgets.getCommunicationSmallListEnabled(value);
    const cta = HelpersClientWidgets.getCommunicationSmallCtaEnabled(value);

    return <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="caption" sx={{color: 'text.secondary'}}>
          {t(`${labelBase}.description`)}
        </Typography>
      </Grid>

      {/* Widget-level toggles. */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
          {t(`${labelBase}.options.title`)}
        </Typography>
        <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
            <HelpersClientWidgets.communicationSmallCountsIcon
              sx={{fontSize: '1.2rem', color: counts ? theme.palette.primary.main : theme.palette.secondary.main}}
            />
            <Switcher
              label={t(`${labelBase}.options.counts`)}
              value={counts}
              greying
              onChange={(c) => onChange({ ...value, counts: c })}
            />
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
            <HelpersClientWidgets.communicationSmallListIcon
              sx={{fontSize: '1.2rem', color: list ? theme.palette.primary.main : theme.palette.secondary.main}}
            />
            <Switcher
              label={t(`${labelBase}.options.list`)}
              value={list}
              greying
              onChange={(c) => onChange({ ...value, list: c })}
            />
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
            <HelpersClientWidgets.communicationSmallCtaIcon
              sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
            />
            <Switcher
              label={t(`${labelBase}.options.cta`)}
              value={cta}
              greying
              onChange={(c) => onChange({ ...value, cta: c })}
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>;
  };

  return Component;
};

// Read-only status: the two toggle icons coloured by on/off so the placed
// box summarises the config at a glance. Same sharing trick as the settings
// component — one implementation, one label base per source.
export const communicationSmallStatus = (labelBase: string) => {

  const StatusComponent: FunctionComponent<{
    value: CommunicationSmallSettingsValue;
    order?: string[];
  }> = ({
    value
  }) => {
    const theme = useTheme();
    const {t} = useTranslation(['translations']);

    const counts = HelpersClientWidgets.getCommunicationSmallCountsEnabled(value);
    const list = HelpersClientWidgets.getCommunicationSmallListEnabled(value);
    const cta = HelpersClientWidgets.getCommunicationSmallCtaEnabled(value);

    return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
        <Box title={t(`${labelBase}.options.counts`)}>
          <HelpersClientWidgets.communicationSmallCountsIcon
            sx={{ fontSize: '1.1rem', color: counts ? theme.palette.primary.main : theme.palette.secondary.main }}
          />
        </Box>
        <Box title={t(`${labelBase}.options.list`)}>
          <HelpersClientWidgets.communicationSmallListIcon
            sx={{ fontSize: '1.1rem', color: list ? theme.palette.primary.main : theme.palette.secondary.main }}
          />
        </Box>
        <Box title={t(`${labelBase}.options.cta`)}>
          <HelpersClientWidgets.communicationSmallCtaIcon
            sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
          />
        </Box>
      </Box>
    </Box>;
  };

  return StatusComponent;
};

const SETTINGS_BASE = 'coach.personal_settings.userdetail_widgets.settings';

export const CoachSettingsWidgetCommunicationNutri = communicationSmallSettings(`${SETTINGS_BASE}.communication_nutri`);
export const CoachSettingsWidgetCommunicationNutriStatus = communicationSmallStatus(`${SETTINGS_BASE}.communication_nutri`);

export const CoachSettingsWidgetCommunicationEhealth = communicationSmallSettings(`${SETTINGS_BASE}.communication_ehealth`);
export const CoachSettingsWidgetCommunicationEhealthStatus = communicationSmallStatus(`${SETTINGS_BASE}.communication_ehealth`);

export const CoachSettingsWidgetCommunicationZorg = communicationSmallSettings(`${SETTINGS_BASE}.communication_zorg`);
export const CoachSettingsWidgetCommunicationZorgStatus = communicationSmallStatus(`${SETTINGS_BASE}.communication_zorg`);
